import { createPortal } from 'react-dom';
import { ReactComponent as CloseIcon } from '../../assets/images/close-icon.svg';
import { ReactComponent as RollbackFullAgentIcon } from '../../assets/images/rollback-full-agent-icon.svg';

interface RollbackConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<boolean>;
  isLoading: boolean;
  error?: string | null;
}

const RollbackConfirmModal = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  error,
}: RollbackConfirmModalProps) => {
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="w-full max-w-[400px] bg-[#1e293b]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.5)] overflow-hidden">
        <div className="relative p-6">
          {!isLoading && (
            <button
              className="absolute right-4 top-4 text-white/40 hover:text-white/80 transition-colors"
              onClick={onClose}
              type="button"
              title="Close"
            >
              <CloseIcon width="14" height="14" />
            </button>
          )}

          <div className="flex flex-col items-center">
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 ${
                isLoading
                  ? 'bg-[#208AEC]/15 shadow-[0_0_25px_rgba(32,138,236,0.3)]'
                  : 'bg-[#208AEC]/10'
              }`}
            >
              {isLoading ? (
                <div className="w-8 h-8 rounded-full border-[3px] border-[#208AEC]/30 border-t-[#208AEC] animate-spin" />
              ) : (
                <RollbackFullAgentIcon className="h-7 w-7 text-[#208AEC] fill-current" />
              )}
            </div>

            <h2 className="mt-4 text-xl font-semibold text-white text-center font-sora">
              {isLoading ? 'Rolling back…' : 'Rollback to this message?'}
            </h2>

            <p className="mt-2 text-sm text-white/60 text-center max-w-lg">
              {isLoading
                ? 'We are restoring the app and chat to this point. Newer messages will be permanently deleted.'
                : 'This will restore the app and chat to this point. Newer messages will be permanently deleted.'}
            </p>
          </div>

          {error ? (
            <div className="mt-6 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl p-3">
              {error}
            </div>
          ) : null}

          <div className="mt-6 min-h-[40px] flex items-center justify-end gap-3">
            {!isLoading ? (
              <>
              <button
                className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 transition-colors"
                type="button"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-xl text-white font-medium transition-colors"
                style={{ backgroundColor: '#27AE60' }}
                type="button"
                onClick={() => {
                  void onConfirm();
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#19875F';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#27AE60';
                }}
              >
                Rollback
              </button>
              </>
            ) : (
              <div className="ml-auto flex items-center justify-center">
                <div className="w-8 h-8 rounded-full border-[3px] border-[#208AEC]/30 border-t-[#208AEC] animate-spin" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RollbackConfirmModal;
